// BANNER DA HOME NO HTML DO SERVIDOR (SD-0020, cs1859 Mister Imóveis — 14/10/2026).
//
// O carrossel de banners (NoveltyPieces) é `dynamic(..., { ssr: false })` + Swiper e vinha dentro
// de um LazyRender: a arte só aparecia depois do JS, do IntersectionObserver e do skeleton — no
// celular o banner pintava ~4 s depois da navegação. Aqui o PRIMEIRO slide sai no HTML do servidor
// como `next/image` com `loading="eager"` (SEM `priority`: o bloco fica abaixo do herói de 100vh,
// e um preload competiria com a imagem do topo — medido no 1859 em 14/10) e, no cliente, o carrossel de
// sempre monta por cima (lazy — o chunk do Swiper só é pedido quando o slide chega a 300px da
// viewport, como fazia o LazyRender) e substitui o slide estático sem pulo:
// mesma imagem (mesma URL/srcset → já está no cache), mesmas classes e a mesma geometria do
// caminho padrão do NoveltyPieces (min-height 500/200, max-height 450/220, cover no desktop e
// contain abaixo de 865px). A troca acontece num useLayoutEffect (antes de pintar) → sem frame
// com o banner duplicado.
//
// Só entra quando `pages/index.tsx` decide que o bloco "banner" é o primeiro do miolo da home
// (ver `bannerPrimeiroDoMiolo`); nos demais layouts o bloco continua byte a byte como era.
// Tenants com geometria própria no NoveltyPieces (proporção natural, teto de altura, override
// de arte) ficam fora: o slide estático reproduz SÓ o caminho padrão.
import dynamic from 'next/dynamic';
import Image from 'next/image';
import Link from 'next/link';
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import styles from './NoveltyPieces.module.css';
import { bannerHomeColado } from '../../../utils/bannerHomeColado';

// `loading: null` de propósito: o placeholder do carrossel é o próprio slide estático.
const NoveltyPieces = dynamic(() => import('./NoveltyPieces'), { ssr: false, loading: () => null });

// Espelha os `site?.id == X` do NoveltyPieces (2291/2422/2523/2551/2603) e os blocos próprios
// do index.tsx (2052/2226): nesses tenants o caminho antigo continua valendo.
export const TENANTS_BANNER_GEOMETRIA_PROPRIA = new Set<number>([2052, 2226, 2291, 2422, 2523, 2551, 2603]);

export const bannerHomeTemSsr = (site: any, banner: any): boolean =>
    Array.isArray(banner)
    && typeof banner[0]?.url === 'string'
    && banner[0].url.length > 0
    && !TENANTS_BANNER_GEOMETRIA_PROPRIA.has(Number(site?.id));

type Props = {
    banner: any;
    site: any;
    bannerMobile?: any;
    bannerDesktop?: any;
    /** preload + fetchpriority=high no 1º slide. Default FALSE (14/10): mesmo sendo o 1º bloco do miolo,
     *  o banner começa abaixo do herói de 100vh — sem priority o slide continua no HTML do servidor com
     *  loading="eager", só não disputa a rede com a imagem do topo. */
    priority?: boolean;
};

// cs2669 (utils/bannerHomeColado): = coladoContainerStyle / naturalBannerStyle / naturalImageStyle do carrossel —
// sem margens, sem a caixa mínima de 500px e a arte inteira na proporção natural.
const COLADO_CONTAINER: React.CSSProperties = { maxWidth: '100%', width: '100%', margin: 0, minHeight: 'auto' };
const COLADO_BANNER: React.CSSProperties = { width: '100%', maxWidth: '100%', maxHeight: 'none', margin: '0 auto' };
const COLADO_IMG: React.CSSProperties = { width: '100%', height: 'auto', maxHeight: 'none', objectFit: 'contain' };

const PrimeiroSlide = forwardRef<HTMLDivElement, { slide: any; site: any; priority: boolean }>(({ slide, site, priority }, ref) => {
    const comLink = slide?.url_link != "1";
    const colado = bannerHomeColado(site);
    // = baseBannerStyle do carrossel (modelo_card 3 não escurece o banner)
    const filtro: React.CSSProperties | undefined = site?.modelo_card == 3 ? { filter: 'none' } : undefined;
    const bannerStyle: React.CSSProperties | undefined = colado ? { ...filtro, ...COLADO_BANNER } : filtro;
    return (
        <div ref={ref} className={styles.container} data-el="novelty" data-ssr="primeiro-banner" style={colado ? COLADO_CONTAINER : undefined}>
            <div className={colado ? styles.cards : `${styles.cards} ${styles.ssrPrimeiro}`} style={{ alignItems: 'center', maxWidth: '100%' }}>
                <div className={`${styles.card} ${styles.ssrCard}`}>
                    {comLink ? (
                        <Link prefetch={false} href={slide?.url_link || '/imoveis'} target='_blank'>
                            <div className={styles.banner} data-el="novelty-banner" style={bannerStyle}>
                                <Image
                                    alt="Banner"
                                    src={slide.url}
                                    width={colado ? 0 : 2000}
                                    height={colado ? 0 : 450}
                                    sizes="100vw"
                                    quality={90}
                                    priority={priority}
                                    loading={priority ? undefined : 'eager'}
                                    className={colado ? undefined : styles.ssrImg}
                                    style={colado ? COLADO_IMG : { width: '100%', height: 'auto' }}
                                />
                            </div>
                        </Link>
                    ) : (
                        <div className={styles.banner1} data-el="novelty-banner" style={colado ? COLADO_BANNER : undefined}>
                            <Image
                                alt="Banner"
                                src={slide.url}
                                width={colado ? 0 : 2000}
                                height={colado ? 0 : 400}
                                sizes="100vw"
                                quality={90}
                                priority={priority}
                                loading={priority ? undefined : 'eager'}
                                style={colado ? COLADO_IMG : {
                                    width: '100%',
                                    height: 'auto',
                                    maxHeight: 400,
                                    objectFit: 'contain',
                                    borderRadius: 12,
                                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
});
PrimeiroSlide.displayName = 'PrimeiroSlide';

const NoveltyPiecesSsr: React.FC<Props> = ({ banner, site, bannerMobile, bannerDesktop, priority = false }) => {
    // pedido = o slide estático chegou a 300px da viewport → baixa o chunk do carrossel (= LazyRender,
    // mas observando o PRÓPRIO slide: um alvo de altura zero depois dele não dispara o observer com segurança)
    const [carrosselPedido, setCarrosselPedido] = useState(false);
    // pronto = o carrossel montou (useLayoutEffect dele) → o slide estático sai antes do próximo paint
    const [carrosselPronto, setCarrosselPronto] = useState(false);
    const ref = useRef<HTMLDivElement | null>(null);
    const primeiro = Array.isArray(banner) ? banner[0] : null;

    useEffect(() => {
        if (carrosselPedido || typeof window === 'undefined') return;
        const el = ref.current;
        if (!el || !('IntersectionObserver' in window)) {
            setCarrosselPedido(true);
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    setCarrosselPedido(true);
                    observer.disconnect();
                }
            },
            { rootMargin: '300px 0px' }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, [carrosselPedido]);

    return (
        <>
            {!carrosselPronto && primeiro ? <PrimeiroSlide ref={ref} slide={primeiro} site={site} priority={priority} /> : null}
            {carrosselPedido ? (
                <NoveltyPieces
                    banner={banner}
                    bannerMobile={bannerMobile}
                    bannerDesktop={bannerDesktop}
                    site={site}
                    onMounted={() => setCarrosselPronto(true)}
                />
            ) : null}
        </>
    );
};

export default NoveltyPiecesSsr;
